import {
  Alert,
  Autocomplete,
  Checkbox,
  Chip,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { MAX_AUDIENCE_COMPANIES } from "shared/organization-messages";
import { type AudienceState, type CompanyOption } from "../message-content";
import useCompanyOptions from "../use-company-options";

type AudienceFieldProps = {
  readonly audience: AudienceState;
  readonly disabled?: boolean;
  readonly onChange: (audience: AudienceState) => void;
};

const isSameCompany = (option: CompanyOption, value: CompanyOption) =>
  option.uuid === value.uuid;

/** All companies, or a searchable multi-select of chosen companies. */
const AudienceField = ({
  audience,
  disabled,
  onChange,
}: AudienceFieldProps) => {
  const { companies, loading, error } = useCompanyOptions();

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        color="primary"
        disabled={disabled}
        value={audience.allCompanies ? "ALL" : "CHOSEN"}
        onChange={(_event, value: string | null) => {
          if (value !== null) {
            onChange({ ...audience, allCompanies: value === "ALL" });
          }
        }}
      >
        <ToggleButton value="CHOSEN">Chosen companies</ToggleButton>
        <ToggleButton value="ALL">All companies</ToggleButton>
      </ToggleButtonGroup>
      {audience.allCompanies ? (
        <Alert severity="warning" variant="outlined">
          Every customer and internal company. Test and demo companies are left
          out. You will type SEND to confirm.
        </Alert>
      ) : (
        <>
          {error !== undefined && (
            <Alert severity="warning">
              Could not load companies: {error.message}
            </Alert>
          )}
          <Autocomplete
            multiple
            disableCloseOnSelect
            disabled={disabled}
            loading={loading}
            options={companies}
            value={[...audience.companies]}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={isSameCompany}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox size="small" checked={selected} />
                {option.name}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key: _tagKey, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={option.uuid}
                    {...tagProps}
                    size="small"
                    label={option.name}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Companies"
                placeholder={
                  audience.companies.length === 0 ? "Search companies" : ""
                }
                helperText={`${audience.companies.length} selected (up to ${MAX_AUDIENCE_COMPANIES})`}
              />
            )}
            onChange={(_event, value) => {
              onChange({ ...audience, companies: value });
            }}
          />
        </>
      )}
    </Stack>
  );
};

export default AudienceField;
